import { ReactNode, useEffect } from 'react'
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react'

import { storageAvailable } from 'utils/storageAvailable'
import { track } from 'utils/analytics'
import { events } from 'analytics/events'

import {
  Button,
  OrderIcon,
  SkipIcon,
  TruckIcon2,
} from '@tovala/component-library'
import { useTypeformPopup } from 'hooks/typeform'
import { UserV1 } from '@tovala/browser-apis-combinedapi'

const onboardingItems = [
  {
    icon: <OrderIcon />,
    text: "Add a meal to your order and we'll automatically save it.",
    title: 'Picking Meals',
  },
  {
    icon: <SkipIcon />,
    text: 'It\'s easy. Just tap the "skip/adjust" button to skip any week.',
    title: 'Skip Anytime',
  },
  {
    icon: <TruckIcon2 />,
    text: 'Add or remove items from your order until Wednesday at 6 p.m. CT (holiday dependent) the week before your delivery.',
    title: 'Order Deadline',
  },
]

const OnboardingModal = ({
  onCloseModal,
  user,
}: {
  onCloseModal(): void
  user: UserV1
}) => {
  let stripeChargeID = undefined
  const latestPurchaseWithCharge = user.productPurchases.find((purchase) => {
    return (
      purchase.payment !== null &&
      purchase.payment.paymentLineItems.find((item) => {
        return item.type === 'charge' && item.stripeChargeID !== ''
      })
    )
  })
  if (latestPurchaseWithCharge && latestPurchaseWithCharge.payment) {
    stripeChargeID = latestPurchaseWithCharge.payment.paymentLineItems.find(
      (item) => {
        return item.type === 'charge'
      },
    )?.stripeChargeID
  }

  const { openTypeformPopup } = useTypeformPopup({
    userID: `${user.id}`,
  })

  const openPostPurchaseSurvey = () => {
    openTypeformPopup({
      hiddenFields: {
        email: user.info.email,
        ...(stripeChargeID && { order_id: stripeChargeID }),
      },
      onClose: () => {
        onCloseModal()
        track(events.CLOSED_POST_PURCHASE_SURVEY)
      },
      onReady: () => {
        track(events.CLOSED_WEB_ONBOARDING_MODAL)
        track(events.VIEWED_POST_PURCHASE_SURVEY)
      },
      onSubmit: () => {
        track(events.COMPLETED_POST_PURCHASE_SURVEY)
      },
      survey: 'postPurchase',
    })
  }

  useEffect(() => {
    if (storageAvailable('localStorage')) {
      localStorage.setItem('viewedOnboarding', 'true')
    }

    track(events.POST_SIGNUP_BREADCRUMBS_DID_APPEAR)
  }, [])

  return (
    <Dialog
      onClose={() => {
        openPostPurchaseSurvey()
      }}
      open={true}
    >
      <div
        className="fixed inset-0 z-30 bg-black opacity-40"
        onClick={() => {
          openPostPurchaseSurvey()
        }}
      />
      <div className="fixed inset-0 z-30 flex items-center justify-center">
        <DialogPanel className="max-w-lg rounded-2xl bg-white p-6 pt-10">
          <DialogTitle className="mb-10 px-3 text-center text-k/28_130">
            Things to Know
          </DialogTitle>

          <div className="space-y-10">
            {onboardingItems.map((item) => {
              return <OnboardingItem key={item.title} {...item} />
            })}

            <div className="flex flex-col">
              <Button
                onClick={() => {
                  openPostPurchaseSurvey()
                }}
                size="large"
              >
                Dismiss
              </Button>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  )
}

export default OnboardingModal

const OnboardingItem = ({
  icon,
  text,
  title,
}: {
  icon: ReactNode
  text: string
  title: string
}) => {
  return (
    <div className="flex space-x-4">
      <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full bg-grey-2">
        <div className="w-10">{icon}</div>
      </div>
      <div className="max-w-xs space-y-2">
        <span className="inline-block border-b-[3px] border-orange-1 pb-2 text-k/16_125 font-semibold">
          {title}
        </span>
        <p className="text-body-sm">{text}</p>
      </div>
    </div>
  )
}
